import React, { useEffect, useRef, useState, useCallback } from "react";
import { t as T } from "i18next";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import FeedItem from "./FeedItem";
import PromoSpot from "./PromoSpot";

/**
 * FeedList — infinite scroll av wall-poster.
 * - Promo-spot hver 5. post (roterende, shuffled pool).
 * - Reels er skjult i beta inntil db.reels + opplasting er på plass.
 */
export default function FeedList({ selected, followMode, search, reloadKey = 0 }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [spots, setSpots] = useState([]);
  const sentinelRef = useRef(null);

  // Hent promo-spots én gang
  useEffect(() => {
    api.get("/feed/promo-spots")
      .then((r) => {
        const pool = r.data?.spots || [];
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        setSpots(shuffled);
      })
      .catch(() => setSpots([]));
  }, []);

  const fetchMore = useCallback(async (reset = false) => {
    if (loading) return;
    if (!reset && done) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        follow_mode: followMode,
        limit: 20,
      };
      if (selected.length > 0) params.categories = selected.join(",");
      if (search) params.search = search;
      if (!reset && cursor) params.cursor = cursor;
      const r = await api.get("/feed/items", { params });
      const newItems = r.data?.items || [];
      setItems((prev) => (reset ? newItems : [...prev, ...newItems]));
      setCursor(r.data?.next_cursor || null);
      setDone(!r.data?.next_cursor);
    } catch (e) {
      setError(e?.response?.data?.detail || T("ui.feed_load_failed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, loading, done, selected, followMode, search]);

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setDone(false);
    fetchMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, followMode, search, reloadKey]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !done) fetchMore();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchMore, loading, done]);

  const rendered = [];
  let spotIdx = 0;
  items.forEach((it, idx) => {
    rendered.push(<FeedItem key={`${it.type}-${it.id}`} item={it} />);
    const pos = idx + 1;
    if (pos % 5 === 0 && spots.length > 0) {
      const spot = spots[spotIdx % spots.length];
      rendered.push(<PromoSpot key={`spot-${idx}-${spot.kind}`} spot={spot} />);
      spotIdx += 1;
    }
  });
  // v237: når feeden er slutt (eller nesten tom) vises resten av promo-kortene nederst.
  if ((done || items.length < 4) && !loading && spotIdx < spots.length) {
    spots.slice(spotIdx).forEach((spot, i) => {
      rendered.push(<PromoSpot key={`spot-tail-${i}-${spot.kind}`} spot={spot} />);
    });
  }

  return (
    <div data-testid="beta-feed-list">
      {rendered}
      {items.length === 0 && !loading && spots.length === 0 && (
        <div className="text-center py-12 text-[var(--cream-faint)] font-meta text-sm">
          {t("beta.empty_feed", "Ingen innhold å vise ennå.")}
        </div>
      )}
      {loading && (
        <div className="text-center py-6 text-[var(--cream-faint)] font-meta text-xs uppercase tracking-wider">
          {t("beta.loading_more", "Laster …")}
        </div>
      )}
      {error && (
        <div className="text-center py-6 text-red-400 text-xs">{error}</div>
      )}
      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
    </div>
  );
}
